import React from 'react';
import {
    Shield,
    Users,
    Flag,
    MessageSquare,
    Calendar,
    FileText,
    TrendingUp
} from 'lucide-react';
const Main = () => {
    const qualifiedBidders = [
        {
            name: "Global IP Partners",
            tasks: 234,
            team: {
                partner: "Sarah Chen",
                associates: 3,
                support: 2
            }
        },
        {
            name: "Tech Law Solutions",
            tasks: 156,
            team: {
                partner: "James Wilson",
                associates: 2,
                support: 2
            }
        }
    ];

    return (
        <div className="space-y-6">
            {/* Pre-engagement Checks */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        <span className='font-bold'>Automated Pre-engagement Checks</span>
                    </div>
                    <div>
                        Instant conflict and availability verification
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {qualifiedBidders.map((firm) => (
                            <div key={firm.name} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="font-medium">{firm.name}</h3>
                                        <p className="text-sm text-gray-500">{firm.tasks} completed tasks</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                            No Conflicts
                                        </div>
                                        <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                            Team Available
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4 mt-4">
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="text-sm text-gray-500">Earliest Start</div>
                                        <div className="font-medium">Immediate</div>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="text-sm text-gray-500">Team Size</div>
                                        <div className="font-medium">{firm.team.associates + firm.team.support} Available</div>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="text-sm text-gray-500">Lead Partner</div>
                                        <div className="font-medium">{firm.team.partner}</div>
                                    </div>
                                </div>

                                <div className="border-t mt-4 pt-4">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-500">
                                            Verified {new Date().toLocaleDateString()}
                                        </div>
                                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                            Request Proposal
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Milestone Structure */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Flag className="w-5 h-5" />
                        <span className='font-bold'>Project Structure</span>
                    </div>
                    <div>
                        Standardized milestone and payment structure
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {['Initial Phase', 'Implementation', 'Completion'].map((phase, index) => (
                            <div key={phase} className="p-4 border rounded-lg">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="font-medium">{phase}</h3>
                                        <p className="text-sm text-gray-500">
                                            {index === 0 ? 'Strategic Planning & Setup' :
                                                index === 1 ? 'Core Work Execution' :
                                                    'Final Delivery & Handover'}
                                        </p>
                                    </div>
                                    <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                        {index === 1 ? '50%' : '25%'} of Total
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm text-gray-500 mb-2">Key Deliverables</div>
                                        <div className="space-y-1 text-sm">
                                            {index === 0 ? (
                                                <>
                                                    <div>• Project Plan</div>
                                                    <div>• Initial Assessment</div>
                                                    <div>• Timeline Definition</div>
                                                </>
                                            ) : index === 1 ? (
                                                <>
                                                    <div>• Core Work Products</div>
                                                    <div>• Progress Reports</div>
                                                    <div>• Quality Checks</div>
                                                </>
                                            ) : (
                                                <>
                                                    <div>• Final Deliverables</div>
                                                    <div>• Documentation</div>
                                                    <div>• Client Sign-off</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500 mb-2">Payment Terms</div>
                                        <div className="text-sm">
                                            {index === 0 ? 'Released upon deliverable acceptance' :
                                                index === 1 ? 'Released in 2 installments' :
                                                    'Released upon final acceptance'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Collaboration Tools */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        <span className='font-bold'>Collaboration Tools</span>
                    </div>
                    <div>
                        Communication and document sharing
                    </div>
                </div>
                <div>
                    <div className="grid grid-cols-2 gap-4">
                        {/* Communication Channels */}
                        <div className="space-y-4 mt-2">
                            <div className="p-4 border rounded-lg h-full">
                                <h3 className="font-medium mb-3">Available Channels</h3>
                                <div className="space-y-2">

                                    <button className="w-full justify-start flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                        <MessageSquare className="w-4 h-4 mr-2" />
                                        Secure Messaging
                                    </button>

                                    <button className="w-full justify-start flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                        <Calendar className="w-4 h-4 mr-2" />
                                        Schedule Meeting
                                    </button>
                                    <button className="w-full justify-start flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                        <FileText className="w-4 h-4 mr-2" />
                                        Document Exchange
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Activity Feed */}
                        <div className="space-y-4 mt-2">
                            <div className="p-4 border rounded-lg h-full">
                                <h3 className="font-medium mb-3">Recent Activity</h3>
                                <div className="space-y-2">
                                    {['New proposal received', 'Document shared', 'Meeting scheduled'].map((activity, index) => (
                                        <div key={index} className="text-sm flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-[#22c55e]' :
                                                index === 1 ? 'bg-blue-500' :
                                                    'bg-[#eab308]'
                                                }`}></div>
                                            {activity}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default Main